import {Component, forwardRef, Input, OnInit, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Tools} from 'linkurious-shared';

const UI_SLIDER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SliderComponent),
  multi: true
};

@Component({
  selector: 'slider',
  template: `<div class="slider-wrapper">
  <div class="slider-min-legend" *ngIf="minLegend">
    {{ minLegend }}
  </div>
  <div class="slider-controls">
    <div #activeLine class="slider-line--active"></div>
    <div class="slider-line"></div>
    <input #rangeInput
           type="range"
           [(ngModel)]="slider_option"
           (change)="updateStyle(rangeInput.value)"
           [value]="valuesArray[selectedIndex]"
           [min]="min"
           [max]="max"
           [step]="step">
    <ul class="slider-intervals">
      <li *ngFor="let value of valuesArray; let i = index"
          [class.active]="selectedIndex === i"
          [class.selected]="selectedIndex >= i"
          (click)="changeIndex(i)">&nbsp;</li>
    </ul>
  </div>
  <div class="slider-max-legend" *ngIf="maxLegend">
    {{ maxLegend }}
  </div>
</div>
`,
  styles: [`.slider-wrapper{display:flex;align-items:center}.slider-min-legend{position:relative;margin-right:15px}.slider-max-legend{position:relative;margin-left:20px}.slider-controls{flex:1;position:relative;width:100%}.slider-line--active{position:absolute;background:#0081e3;top:0;width:10%;height:2px;z-index:8}.slider-line{background:#dfdfdf;width:100%;height:2px;z-index:5}input{width:calc(100% + 16px);position:absolute;top:-15px;height:30px;margin:0;-webkit-appearance:none;background:0 0;z-index:9}input:focus{background:0 0;outline:0;border:0}input::-webkit-slider-runnable-track{width:100%;height:2px;cursor:pointer;background:0 0;border-color:transparent;color:transparent;z-index:2}input::-webkit-slider-thumb{-webkit-appearance:none;width:20px;height:20px;margin-top:-8px;margin-left:-8px;border-radius:50%;background:#fff;border:1px solid #878787;box-shadow:0 2px 4px rgba(0,0,0,.16);cursor:pointer;z-index:10}input::-moz-focus-outer{border:0}input::-moz-range-track{width:100%;height:2px;cursor:pointer;background:0 0;border-color:transparent;color:transparent;z-index:2}input::-moz-range-thumb{width:20px;height:20px;margin-top:-8px;border-radius:50%;background:#fff;border:1px solid #878787;box-shadow:0 2px 4px rgba(0,0,0,.16);cursor:pointer;z-index:10}input::-ms-track{width:100%;height:1px;cursor:pointer;background:0 0;border-color:transparent;color:transparent;z-index:2}input::-ms-thumb{width:20px;height:20px;margin-top:-2px;border-radius:50%;background:#fff;border:1px solid #878787;box-shadow:0 2px 4px rgba(0,0,0,.16);cursor:pointer;z-index:10}@-moz-document url-prefix(){input{width:100%}}@media screen and (-ms-high-contrast:active),(-ms-high-contrast:none){input{width:100%}}@supports (-ms-accelerator:true){input{width:100%}}.slider-intervals{position:absolute;top:-4px;left:0;display:flex;justify-content:space-between;width:100%;margin:0;padding:0;list-style:none}.slider-intervals li{position:relative;flex:0 0 8px;color:#b2b2b2;font-size:14px;cursor:pointer}.slider-intervals li::before{position:absolute;align-self:center;right:0;left:0;top:1px;content:"";width:8px;height:8px;background:#dfdfdf;border-radius:50%;z-index:8}.slider-intervals .active{color:#0081e3}.slider-intervals .selected::before{background:#0081e3}.slider-intervals .active.selected::before{display:none}`],
  providers: [UI_SLIDER_VALUE_ACCESSOR]
})
export class SliderComponent implements OnInit, ControlValueAccessor {

  @ViewChild('activeLine') activeLine;

  @Input('minLegend') minLegend: string;
  @Input('maxLegend') maxLegend: string;
  @Input('min') min: number;
  @Input('max') max: number;
  @Input('intervals') intervals: number;

  private _sheet: any;
  private _value: number;

  public step;
  public valuesArray: Array<number> = [];
  public selectedIndex: number = 0;

  constructor() {}

  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  ngOnInit() {
    this.processIntervals();
    this.updateStyle(this.valuesArray[0]);
  }

  private processIntervals() {
    this.intervals = ( Tools.isDefined(this.intervals) && this.intervals > 0)
      ? this.intervals
      : 1;
    this._value = this.min;
    this.step = ( this.max - this.min ) / this.intervals;
    this.valuesArray = [];
    this.valuesArray.push(this.min);
    for (let i = 1; i < this.intervals; i++) {
      this.valuesArray.push( ((this.valuesArray[i - 1] * 100) + (this.step * 100)) / 100 );
    }
    this.valuesArray.push(this.max);
  }

  private getTrackStyle(value: number) {
    this.selectedIndex = this.valuesArray.indexOf(value);
    if(this.selectedIndex !== 0) {
      let percentage = this.valuesArray.indexOf(value) * (100 / (this.valuesArray.length - 1));
      this.activeLine.nativeElement.style.width = percentage + '%';
    } else {
      this.activeLine.nativeElement.style.width = 0;
    }
  }

  public updateStyle(value: any) {
    this.getTrackStyle(+value);
  };

  public changeIndex(index: number) {
    this.slider_option = this.valuesArray[index];
    this.getTrackStyle(this.valuesArray[index]);
  };

  set slider_option(opt: number) {
    if( opt ) {
      this._value = opt;
      this.updateStyle(opt);
    }
    this.onChangeCallback(opt);
  }

  get slider_option() {
    return this._value;
  }

  writeValue(obj: number): void {
    if ( obj ) {
      this.slider_option = obj;
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
}
